// useVideoDetails.ts — the attached clip's Title / Category / Short state (see
// ComposerVideoDetails.tsx for the owner rules).
import { useEffect, useState } from 'react';
import type { VideoCategoryId } from '@/core/models/post';
import { videoFeedService } from '@/core/services/videoFeedService';

export const SHORT_MAX_MS = 60_000;

export const VIDEO_CATEGORIES: { id: VideoCategoryId; label: string }[] = [
  { id: 'gaming', label: 'Gaming' },
  { id: 'news', label: 'News' },
  { id: 'education', label: 'Education' },
  { id: 'music', label: 'Music' },
  { id: 'tech', label: 'Tech' },
  { id: 'sports', label: 'Sports' },
];

/** 1.27.60 uploader permissions, with the server's defaults. */
export interface VideoPermissions {
  allowDownload: boolean;
  allowEmbed: boolean;
  allowClips: boolean;
}
export const DEFAULT_VIDEO_PERMISSIONS: VideoPermissions = {
  allowDownload: false,
  allowEmbed: true,
  allowClips: true,
};

export interface VideoDetails extends VideoPermissions {
  title: string;
  category: VideoCategoryId | '';
  short: boolean;
}

const GUESS_DELAY_MS = 600;

/**
 * Details state for one attached clip. Reset whenever the clip changes (`clipKey`).
 * `bodyText` feeds the category guess; `autoShort` is the member's setting.
 *
 * Any clip of 60 s or less MAY be a Short (the switch shows), but the classifier only
 * pre-ticks it for a portrait or square clip (owner, 2026-09-26, after the first real
 * upload — a 9 s landscape screen recording — landed in the vertical Shorts player). A
 * landscape clip goes to the ordinary watch page unless the uploader ticks Short.
 */
export function useVideoDetails(
  clipKey: File | null,
  durationMs: number | null,
  portrait: boolean | null,
  bodyText: string,
  autoShort: boolean,
) {
  const [state, setState] = useState<{
    key: File | null;
    title: string;
    category: VideoCategoryId | '';
    categoryTouched: boolean;
    short: boolean | null;
    permissions: VideoPermissions;
  }>({
    key: null,
    title: '',
    category: '',
    categoryTouched: false,
    short: null,
    permissions: DEFAULT_VIDEO_PERMISSIONS,
  });

  const current =
    state.key === clipKey
      ? state
      : {
          key: clipKey,
          title: '',
          category: '' as const,
          categoryTouched: false,
          short: null,
          permissions: DEFAULT_VIDEO_PERMISSIONS,
        };

  const canBeShort = durationMs !== null && durationMs <= SHORT_MAX_MS;
  const short = canBeShort && (current.short ?? (autoShort && portrait === true));

  // Pre-fill the category from the post text until the uploader picks one themselves.
  useEffect(() => {
    if (!clipKey || current.categoryTouched || !bodyText.trim()) return;
    let live = true;
    const t = setTimeout(() => {
      void videoFeedService.guessCategory(bodyText).then((guess) => {
        if (!live || !guess) return;
        setState((prev) =>
          prev.key === clipKey && prev.categoryTouched
            ? prev
            : {
                ...(prev.key === clipKey ? prev : { ...current }),
                key: clipKey,
                category: guess,
              },
        );
      });
    }, GUESS_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
    // `current` is derived each render; the guess only depends on the text and the clip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyText, clipKey, current.categoryTouched]);

  return {
    details: {
      title: current.title,
      category: current.category,
      short,
      ...current.permissions,
    } as VideoDetails,
    canBeShort,
    setTitle: (title: string) => setState({ ...current, key: clipKey, title }),
    setCategory: (category: VideoCategoryId | '') =>
      setState({ ...current, key: clipKey, category, categoryTouched: true }),
    setShort: (next: boolean) => setState({ ...current, key: clipKey, short: next }),
    setPermission: (name: keyof VideoPermissions, value: boolean) =>
      setState({
        ...current,
        key: clipKey,
        permissions: { ...current.permissions, [name]: value },
      }),
  };
}
